(() => {
  const STORAGE_KEY = 'metrokenshin:wishlist:v1';

  const readWishlist = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(saved) ? saved.filter((item) => item && item.handle) : [];
    } catch (_error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: items }));
  };

  const productFromButton = (button) => ({
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    price: button.dataset.productPrice,
    variantId: button.dataset.productVariant,
    available: button.dataset.productAvailable === 'true'
  });

  const updateControls = (items = readWishlist()) => {
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = handles.has(button.dataset.productHandle);
      const title = button.dataset.productTitle || 'this item';
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = active ? 'Saved to wishlist' : 'Add to wishlist';
    });

    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = items.length;
      count.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-label', `Wishlist with ${items.length} ${items.length === 1 ? 'item' : 'items'}`);
    });
  };

  const toggleProduct = (button) => {
    const product = productFromButton(button);
    const items = readWishlist();
    const index = items.findIndex((item) => item.handle === product.handle);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(product);
    writeWishlist(items);
  };

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const createWishlistCard = (product) => {
    const card = makeElement('article', 'product-card wishlist-card');

    const media = makeElement('a', 'product-card__media');
    media.href = product.url;
    media.setAttribute('aria-label', `View ${product.title}`);
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      media.append(image);
    } else {
      media.append(makeElement('div', 'product-card__placeholder', 'Artwork coming soon'));
    }
    card.append(media);

    const remove = makeElement('button', 'wishlist-button wishlist-card__remove');
    remove.type = 'button';
    remove.dataset.removeWishlist = product.handle;
    remove.setAttribute('aria-label', `Remove ${product.title} from wishlist`);
    remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z"/></svg>';
    remove.setAttribute('aria-pressed', 'true');
    card.append(remove);

    const info = makeElement('div', 'product-card__info');
    const title = makeElement('h3', 'product-card__title');
    const titleLink = makeElement('a', '', product.title);
    titleLink.href = product.url;
    title.append(titleLink);
    info.append(title);

    const actions = makeElement('div', 'wishlist-card__actions');
    actions.append(makeElement('span', 'product-card__price', product.price));
    if (product.available && product.variantId) {
      const add = makeElement('button', 'wishlist-card__cart', 'Add to cart');
      add.type = 'button';
      add.dataset.wishlistAdd = product.variantId;
      actions.append(add);
    } else {
      actions.append(makeElement('span', 'product-card__badge-inline', 'Sold out'));
    }
    info.append(actions);
    card.append(info);
    return card;
  };

  const renderWishlistPage = (items = readWishlist()) => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    const empty = document.querySelector('[data-wishlist-empty]');
    const status = document.querySelector('[data-wishlist-status]');
    grid.replaceChildren(...items.map(createWishlistCard));
    empty.hidden = items.length !== 0;
    status.textContent = items.length ? `${items.length} saved ${items.length === 1 ? 'print' : 'prints'}` : '';
  };

  const addToCart = async (button) => {
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Adding...';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.wishlistAdd), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add item');
      button.textContent = 'Added';
      window.setTimeout(() => window.location.assign('/cart'), 350);
    } catch (_error) {
      button.textContent = 'Try again';
      button.disabled = false;
      window.setTimeout(() => { button.textContent = original; }, 1800);
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      toggleProduct(toggle);
      return;
    }

    const remove = event.target.closest('[data-remove-wishlist]');
    if (remove) {
      writeWishlist(readWishlist().filter((item) => item.handle !== remove.dataset.removeWishlist));
      return;
    }

    const add = event.target.closest('[data-wishlist-add]');
    if (add) addToCart(add);
  });

  document.addEventListener('wishlist:change', (event) => {
    updateControls(event.detail);
    renderWishlistPage(event.detail);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      const items = readWishlist();
      updateControls(items);
      renderWishlistPage(items);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    const items = readWishlist();
    updateControls(items);
    renderWishlistPage(items);
  });
})();
